export function formatDate(date: Date | string | undefined) {
  if (!date) {
    return "";
  }

  if (typeof date === "string") {
    date = new Date(date);
  }

  return date.toLocaleString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatCalendarDate(value?: string | null): string {
  if (!value) {
    return "";
  }
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return formatDate(new Date(year, month - 1, day));
}

export function daysDifferenceBetweenDates(dateOne: Date, dateTwo: Date): number {
  const timeDifference = dateTwo.getTime() - dateOne.getTime();
  const daysDifference = timeDifference / (1000 * 3600 * 24);
  return Math.abs(Math.round(daysDifference));
}

export function formatDateTime(value: unknown): string {
  if (!value) return "—";
  const date = typeof value === "number" ? new Date(value * 1000) : new Date(value as string);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function formatClockTime(value?: string | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "";
  }
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (date.toDateString() === new Date().toDateString()) {
    return time;
  }
  return `${date.toLocaleDateString([], { weekday: "short" })} ${time}`;
}

export function calendarDateIn(timeZone?: string): string {
  try {
    return new Date().toLocaleDateString("en-CA", { timeZone });
  } catch {
    return new Date().toLocaleDateString("en-CA");
  }
}

export function clockTimeIn(timeZone?: string): string {
  try {
    return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone, timeZoneName: "short" });
  } catch {
    return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
}

export function formatHoursMinutes(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${totalSeconds}s`;
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
}

export function shortTimeAgo(value?: string | null): string {
  if (!value) {
    return "";
  }
  const then = new Date(value).getTime();
  if (isNaN(then)) {
    return "";
  }
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) {
    return "now";
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d`;
  }
  return `${Math.floor(days / 7)}w`;
}

export function daysSince(value?: string | null): number | null {
  if (!value) {
    return null;
  }
  const then = new Date(value).getTime();
  if (isNaN(then)) {
    return null;
  }
  return Math.floor((Date.now() - then) / (1000 * 3600 * 24));
}
